/* Core pages and guards: RequireAuth, Welcome, NotFound, Start, FeatureScreen, ErrorBoundary, OfflineBanner. */
import { Component, Suspense, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { Compass, LoaderCircle, Wifi, WifiOff } from 'lucide-react';
import { Banner, Button, EmptyState, Skeleton } from '../ui';
import { API } from '../lib/endpoints';
import type { FeatureDescriptor } from './feature';
import { CREATE_IDS } from './nav';
import { aliasTarget, useOnboardingGate } from './gate';
import { useSession } from './session';

/* ---------- error boundary ---------- */
interface EBProps { children: ReactNode; label?: string }
interface EBState { error: Error | null }
export class ErrorBoundary extends Component<EBProps, EBState> {
  state: EBState = { error: null };
  static getDerivedStateFromError(error: Error): EBState { return { error }; }
  componentDidCatch(error: Error, info: ErrorInfo) {
    // Reported to the console for developers; PLAT-05 telemetry hooks in here later.
    console.error('Screen error', error, info.componentStack);
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="screen-body" role="alert">
        <Banner
          tone="danger"
          title={`${this.props.label ?? 'This screen'} hit an error`}
          action={<Button size="sm" onClick={() => this.setState({ error: null })}>Try again</Button>}
        >
          {this.state.error.message || 'Something went wrong.'} Your other screens and any running jobs are not affected.
        </Banner>
      </div>
    );
  }
}

/* ---------- loading ---------- */
export function ScreenLoading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="screen-body" aria-busy="true">
      <div className="msg-status" role="status"><LoaderCircle size={16} className="spin" aria-hidden="true" style={{ animation: 'cx-spin 1.2s linear infinite' }} /> {label}</div>
      <div className="stack" style={{ maxWidth: 760, marginTop: 16 }}>
        <Skeleton height={56} /><Skeleton height={120} /><Skeleton height={56} />
      </div>
    </div>
  );
}

/* ---------- feature mount ---------- */
export function FeatureScreen({ feature }: { feature: FeatureDescriptor }) {
  const Screen = feature.screen;
  const { pathname } = useLocation();
  useEffect(() => { document.title = `${feature.title} · Crosspoint AI +`; }, [feature.title]);
  return (
    <div className="route-root" data-feature={feature.id}>
      <ErrorBoundary key={pathname.split('/')[1]} label={feature.title}>
        <Suspense fallback={<ScreenLoading label={`Opening ${feature.title}…`} />}>
          <Screen />
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}

/* ---------- auth gate ---------- */
/* Signed-out users go to /welcome (keeping where they were going). Signed-in users who have not finished
   the required onboarding (a Google account without a date of birth: the 18+ step, or the usage-mode step)
   are sent back to that step first (welcome feature gate; resumable on any device). */
export function RequireAuth() {
  const s = useSession();
  const loc = useLocation();
  const gate = useOnboardingGate();
  const from = loc.pathname + loc.search;
  if (s.status === 'loading') return <div className="shell"><ScreenLoading label="Signing you in…" /></div>;
  if (s.status !== 'authenticated') return <Navigate to="/welcome" replace state={{ from }} />;
  if (gate.checking) return <div className="shell"><ScreenLoading label="Checking your account…" /></div>;
  if (gate.redirect && gate.redirect !== loc.pathname) return <Navigate to={gate.redirect} replace state={{ from }} />;
  return <Outlet />;
}

/** URLs the API sends people to (`/onboarding/age?next=`, `/sign-in?error=`, `/reset-password?token=`). */
export function AliasRedirect() {
  const loc = useLocation();
  return <Navigate to={aliasTarget(loc.pathname, loc.search)} replace state={loc.state} />;
}

/* ---------- welcome (signed out) ---------- */
export function Welcome({ features }: { features: FeatureDescriptor[] }) {
  const s = useSession();
  const nav = useNavigate();
  const loc = useLocation();
  const from = (loc.state as { from?: string } | null)?.from ?? '/';
  const has = (route: string) => features.some((f) => f.route === route);
  useEffect(() => { document.title = 'Welcome · Crosspoint AI +'; }, []);
  if (s.status === 'authenticated') return <Navigate to={from === '/welcome' ? '/' : from} replace />;
  return (
    <main className="shell" style={{ overflow: 'auto' }}>
      <div style={{ margin: 'auto', width: 'min(520px, 100%)', padding: 16 }}>
        <div className="card stack" style={{ padding: 24, borderRadius: 'var(--r-xl)', gap: 16 }} data-cap="PLAT-08">
          <span className="logo lg" aria-hidden="true">+</span>
          <h1 style={{ font: 'var(--t-headline-m)' }}>Welcome to Crosspoint AI +</h1>
          <p className="muted">Research with sources, agents that act for you after you approve, and docs, slides, sheets and media from one conversation.</p>
          {s.status === 'unreachable' && (
            <Banner tone="warn" title="Can't reach the Crosspoint AI + service" action={<Button size="sm" onClick={() => void s.refresh()}>Try again</Button>}>
              Nothing was lost. Check your connection{import.meta.env.DEV ? ', or start the API on 127.0.0.1:3130' : ''}, then try again.
            </Banner>
          )}
          <div className="stack" style={{ gap: 8 }}>
            <Button variant="primary" size="lg" block onClick={() => nav(has('/signup') ? '/signup' : '/welcome')} disabled={!has('/signup')}>Create account</Button>
            <a className="btn lg block" href={API.googleStart(from === '/welcome' ? '/' : from)}>Continue with Google</a>
            <Button variant="ghost" size="lg" block onClick={() => nav('/signin')} disabled={!has('/signin')}>Sign in</Button>
          </div>
          <p className="small muted">18+ only. By continuing you agree to the terms and privacy policy.</p>
        </div>
      </div>
    </main>
  );
}

/* ---------- start (no features yet) and 404 ---------- */
export function Start({ features }: { features: FeatureDescriptor[] }) {
  const first = features.find((f) => f.id === 'home') ?? features.find((f) => !f.bare && !f.public && !f.hidden);
  if (first) return <Navigate to={first.route} replace />;
  return (
    <div className="screen-body">
      <EmptyState
        icon={Compass}
        title="No screens installed yet"
        purpose="Feature folders under web/src/features add screens to this shell. Open the example to see the pattern."
        action={<a className="btn primary" href="/?examples=1">Show the example feature</a>}
        example="features/_example"
      />
    </div>
  );
}

export function NotFound() {
  const loc = useLocation();
  useEffect(() => { document.title = 'Not found · Crosspoint AI +'; }, []);
  return (
    <div className="screen-body">
      <EmptyState
        icon={Compass}
        title="This page does not exist"
        purpose={`Nothing lives at ${loc.pathname}. It may have moved, or the feature is not installed yet.`}
        action={<Link className="btn primary" to="/">Go home</Link>}
      />
    </div>
  );
}

/* ---------- Create hub: the one place that gathers everything you can make ---------- */
export function CreateHub({ features }: { features: FeatureDescriptor[] }) {
  useEffect(() => { document.title = 'Create · Crosspoint AI +'; }, []);
  const list = CREATE_IDS.map((id) => features.find((f) => f.id === id)).filter((f): f is FeatureDescriptor => !!f);
  return (
    <div className="screen-body">
      <div className="reading stack" style={{ gap: 24 }}>
        <h1 className="screen-title">Create</h1>
        <p className="muted">Pick what to make. Each one opens its own workspace.</p>
        {list.length ? (
          <ul className="create-cards" aria-label="What to create">
            {list.map((f) => {
              const Icon = f.icon;
              return (
                <li key={f.id}>
                  <Link to={f.route} className="create-card"><Icon size={32} aria-hidden="true" /><span><b>{f.title}</b>{f.commands?.[0] && <span className="muted">{f.commands[0].title}</span>}</span></Link>
                </li>
              );
            })}
          </ul>
        ) : <p className="muted">Nothing to create with is installed yet.</p>}
      </div>
    </div>
  );
}

/* ---------- offline ---------- */
export function useOnline(): boolean {
  const [on, setOn] = useState(typeof navigator === 'undefined' ? true : navigator.onLine);
  useEffect(() => {
    const up = () => setOn(true), down = () => setOn(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);
  return on;
}

export function OfflineBanner() {
  const online = useOnline();
  const [back, setBack] = useState(false);
  const [wasOffline, setWasOffline] = useState(!online);
  useEffect(() => {
    if (!online) { setWasOffline(true); setBack(false); return; }
    if (!wasOffline) return;
    setBack(true);
    setWasOffline(false);
    const t = setTimeout(() => setBack(false), 4000);
    return () => clearTimeout(t);
  }, [online]); // eslint-disable-line react-hooks/exhaustive-deps
  if (online && !back) return <div className="sr-only" role="status" aria-live="polite" />;
  return (
    <div className="gbanner" role="status" aria-live="polite" data-cap="PLAT-02" data-state={online ? 'back' : 'offline'}>
      {online ? <Wifi size={16} aria-hidden="true" /> : <WifiOff size={16} aria-hidden="true" />}
      {online
        ? <span><b>Back online.</b> Anything you queued is being sent now.</span>
        : <span><b>You're offline.</b> Saved chats still open from this device; drafts are kept and messages send when you reconnect. Running jobs keep going on the server.</span>}
    </div>
  );
}
